import * as path from 'node:path';
import { editSnippetToUnifiedDiff } from '../../../files/diff/unified-diff.js';
import type { NameStatusEntry } from '../../changes/parse/types.js';
import { execGitFile } from '../../git-exec.js';
import { readGitBlobAsUtf8, readWorkspaceFileAsUtf8 } from './files.js';
import type { UntrackedMove } from './move-pairs.js';

export type SessionFileChangeMessage = {
  type: 'session_file_change';
  sessionId: string;
  runId: string;
  path: string;
  movedFromPath?: string;
  oldText?: string;
  newText?: string;
  patchContent: string;
};

export async function buildTrackedChangeMessage(options: {
  repoPath: string;
  baselineRev: string;
  entry: NameStatusEntry;
  displayPath: (rel: string) => string;
  sessionId: string;
  runId: string;
}): Promise<SessionFileChangeMessage | null> {
  const { repoPath, baselineRev, entry, displayPath, sessionId, runId } = options;
  const rel = entry.pathInRepo;
  const oldRel = entry.oldPathInRepo;
  const diffArgs =
    entry.change === 'moved' && oldRel
      ? ['diff', '--no-color', '-M', baselineRev, '--', oldRel, rel]
      : ['diff', '--no-color', '-M', baselineRev, '--', rel];
  const { stdout: patchContent } = await execGitFile(diffArgs, { cwd: repoPath });
  if (!patchContent.trim()) return null;

  const workspaceFilePath = path.join(repoPath, rel);
  const newText = entry.change === 'removed' ? '' : await readWorkspaceFileAsUtf8(workspaceFilePath);
  const oldText = entry.change === 'added' ? '' : await readGitBlobAsUtf8(repoPath, baselineRev, oldRel ?? rel);

  return {
    type: 'session_file_change',
    sessionId,
    runId,
    path: displayPath(rel),
    ...(oldRel ? { movedFromPath: displayPath(oldRel) } : {}),
    patchContent,
    ...(oldText !== undefined ? { oldText } : {}),
    ...(newText !== undefined ? { newText } : {}),
  };
}

export function buildUntrackedChangeMessage(options: {
  rel: string;
  displayPath: (rel: string) => string;
  sessionId: string;
  runId: string;
  newText: string;
  moved?: UntrackedMove;
}): SessionFileChangeMessage {
  const { rel, displayPath, sessionId, runId, moved } = options;
  const newText = moved?.newText ?? options.newText;
  const oldText = moved?.oldText ?? '';
  const patchContent = editSnippetToUnifiedDiff(displayPath(rel), oldText, newText);

  return {
    type: 'session_file_change',
    sessionId,
    runId,
    path: displayPath(rel),
    ...(moved ? { movedFromPath: displayPath(moved.oldRel) } : {}),
    oldText,
    patchContent,
    newText,
  };
}
